import { PriorityHigh, PriorityLow, PriorityMedium, X } from '@griply/assets';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { usePopupMenuArrowNavigation } from 'shared/hooks/use-popup-menu-arrow-navigation';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

export type PriorityPickerMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  value?: TaskPriorityOptions | null;
  onClose: () => void;
  onChange: (value: TaskPriorityOptions | null) => void;
};

export const PriorityPickerMenu: React.FC<PriorityPickerMenuProps> = ({
  value,
  onChange,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation();

  const onClear = () => {
    onChange(null);
    onClose();
  };

  const onHigh = () => {
    onChange(TaskPriorityOptions.High);
    onClose();
  };

  const onMedium = () => {
    onChange(TaskPriorityOptions.Medium);
    onClose();
  };

  const onLow = () => {
    onChange(TaskPriorityOptions.Low);
    onClose();
  };
  const menuRef = React.useRef<HTMLDivElement>(null);

  usePopupMenuArrowNavigation({ containerRef: menuRef });
  return (
    <PopupMenu ref={menuRef} onOutside={onClose} {...rest}>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onHigh}
            start={<Icon icon={PriorityHigh} />}
            end={
              value === TaskPriorityOptions.High ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '1'])}
          >
            {t('task.priority.options.high.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onMedium}
            start={<Icon icon={PriorityMedium} />}
            end={
              value === TaskPriorityOptions.Medium ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '2'])}
          >
            {t('task.priority.options.medium.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLow}
            start={<Icon icon={PriorityLow} />}
            end={
              value === TaskPriorityOptions.Low ? (
                <Icon icon={Check} />
              ) : undefined
            }
            shortcut={getPlatformShortcut(['Shift', '3'])}
          >
            {t('task.priority.options.low.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onClear}
            start={<Icon icon={X} />}
            end={value === null ? <Icon icon={Check} /> : undefined}
            shortcut={getPlatformShortcut(['Shift', '4'])}
          >
            {t('task.priority.options.none.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
